import React, { useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, X } from "lucide-react";

const DISMISS_KEY = "xz-streak-dismissed";

/**
 * Evening "streak at risk" banner. Shows when `active` (today is unmarked and
 * there is progress worth protecting), the local hour has reached
 * `reminderHour`, and it hasn't been dismissed today (per device).
 */
const StreakBanner = ({ active, reminderHour = 19, todayKey, onMarkToday }) => {
  const [hour, setHour] = useState(() => new Date().getHours());
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(DISMISS_KEY) === todayKey;
    } catch (e) {
      return false;
    }
  });

  useEffect(() => {
    const id = setInterval(() => setHour(new Date().getHours()), 60 * 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    try {
      setDismissed(localStorage.getItem(DISMISS_KEY) === todayKey);
    } catch (e) {
      setDismissed(false);
    }
  }, [todayKey]);

  if (!active || dismissed || hour < reminderHour) return null;

  const dismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISS_KEY, todayKey);
    } catch (e) {
      /* ignore */
    }
  };

  return (
    <div
      className="xz-hide-in-focus mb-4 rounded-2xl border-2 border-amber-500/40 bg-amber-500/10 px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3"
      role="status"
    >
      <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-amber-200 font-semibold">Your streak is at risk</div>
        <div className="text-xs text-amber-300/80">
          It's past {reminderHour}:00 and today isn't marked yet.
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          type="button"
          onClick={onMarkToday}
          className="tap px-3 py-2 rounded-xl bg-amber-400 hover:bg-amber-300 text-black text-sm font-semibold flex items-center gap-1.5"
        >
          <CheckCircle2 className="w-4 h-4" />
          Mark today done
        </button>
        <button
          type="button"
          onClick={dismiss}
          className="tap p-2 rounded-xl text-amber-300/80 hover:text-amber-200 hover:bg-amber-500/10"
          aria-label="Dismiss for today"
          title="Dismiss for today"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default StreakBanner;
